<template>
    <AppDialog type="full-width-modal" closeable @close="showPromotionModal = false" :show="showPromotionModal">
        <div
            class="md:rounded-3xl flex flex-col md:flex-row max-h-[480px] md:max-w-[936px] md:max-h-full overflow-auto"
        >
            <div
                class="order-2 md:order-1 w-full md:rounded-s-3xl bg-gray-900 md:bg-gray-950 relative md:max-w-[392px] lg:max-w-[354px]"
            >
                <div
                    :class="shadowColor"
                    class="blur-3xl absolute w-[422px] h-[422px] -top-80 rounded-[422px] left-1/2 -translate-x-1/2 hidden md:block"
                ></div>
                <div class="relative px-4 md:px-6 py-6 flex flex-col justify-between h-[420px] xl:h-full">
                    <div class="">
                        <div
                            class="flex flex-row md:flex-col mb-4 md:mb-0"
                            :class="hasBadge ? 'space-x-2 md:space-x-0' : ''"
                        >
                            <NuxtImg
                                src="/svg/acl-icons/standard-icon.svg"
                                class="size-8 md:size-10 md:mb-4"
                                :class="hasBadge ? '' : ' hidden md:block md:invisible'"
                            />
                            <h1
                                class="md:text-title-lg xl:text-display-md font-bold tracking-tighter text-gray-100 text-title-md md:mb-6"
                            >
                                {{ promotionalData.title }}
                            </h1>
                        </div>

                        <p
                            class="text-body-md xl:text-body-lg text-gray-100 leading-6"
                            :class="promotionalData.options ? 'mb-6' : 'mb-5 lg:mb-8'"
                        >
                            {{ promotionalData.description }}
                        </p>
                        <p
                            class="text-body-md xl:text-body-lg text-gray-100 leading-6"
                            :class="promotionalData.options ? 'mb-6' : 'mb-20 lg:mb-0'"
                        >
                            {{ promotionalData.secondDescription }}
                        </p>
                        <ul :class="promotionalData.options ? 'mb-20 lg:mb-4' : ''" class="flex flex-col space-y-6">
                            <li
                                v-for="option in promotionalData.options"
                                class="text-body-lg text-gray-400 flex space-x-2 items-center"
                            >
                                <Icon :name="`tabler:${option.icon}`" class="w-6 h-6 text-brand-300" />
                                <p class="">{{ option.text }}</p>
                            </li>
                        </ul>
                    </div>
                    <div class="md:block hidden">
                        <slot name="extraButton" />
                        <div @click="navigate(promotionalData.cta.link)" :id="promotionalData.cta.id">
                            <AppButton
                                color="white"
                                variant="outlined"
                                :id="promotionalData.cta.id"
                                size="lg"
                                :customGradient="hasGradientButton ? 'gradient' : ''"
                                block
                                >{{ promotionalData.cta.text }}</AppButton
                            >
                        </div>
                    </div>
                </div>
            </div>

            <div class="md:rounded-e-3xl md:overflow-hidden relative order-1 md:order-2 w-full bg-gray-950">
                <NuxtImg
                    :placeholder="backgroundBlurImgSrc"
                    :src="backgroundImgSrc"
                    class="h-full w-full md:w-[312px] xl:w-[590px] md:rounded-e-3xl"
                />
                <div class="md:block hidden">
                    <Icon
                        @click="showPromotionModal = false"
                        name="tabler:x"
                        class="absolute w-6 h-6 text-black right-6 top-6 cursor-pointer"
                    />
                </div>
            </div>
        </div>
        <div class="p-4 block md:hidden bg-gray-900 fixed bottom-0 box-shadow w-full">
            <slot name="extraButton" />
            <div @click="navigate(promotionalData.cta.link)" :id="promotionalData.cta.id">
                <AppButton
                    color="white"
                    variant="outlined"
                    :id="promotionalData.cta.id"
                    size="lg"
                    :customGradient="hasGradientButton ? 'gradient' : ''"
                    block
                    >{{ promotionalData.cta.text }}</AppButton
                >
            </div>
        </div>
    </AppDialog>
</template>

<script setup lang="ts">
import type { IPromotionData } from "~/types/global/result.type";

interface IProps {
    promotionalData: IPromotionData;
    shadowColor: string;
    hasGradientButton?: boolean;
    hasBadge?: boolean;
}

const props = withDefaults(defineProps<IProps>(), {
    hasBadge: true,
});
const showPromotionModal = defineModel("showPromotionModal", { default: true });

const { width } = useWindowSize();

const backgroundImgSrc = computed(() => {
    if (width.value >= 1440) {
        return props.promotionalData.backgroundImages.desktopSrc;
    }
    if (width.value >= 640) {
        return props.promotionalData.backgroundImages.tabletSrc;
    } else {
        return props.promotionalData.backgroundImages.mobileSrc;
    }
});
const backgroundBlurImgSrc = computed(() => {
    if (width.value >= 1440) {
        return props.promotionalData.backgroundImages.desktopBlurSrc;
    }
    if (width.value >= 640) {
        return props.promotionalData.backgroundImages.tabletBlurSrc;
    } else {
        return props.promotionalData.backgroundImages.mobileBlurSrc;
    }
});
const localePath = useLocalePath();

const navigate = (payload: string) => {
    navigateTo(localePath(payload));
    showPromotionModal.value = false;
};
</script>

<style scoped>
.box-shadow {
    box-shadow: 0px -4px 16px 0px rgba(0, 0, 0, 0.25);
}
.gradient {
    box-shadow: 2px 0px 8.4px 0px rgba(244, 149, 124, 0.44), 0px 9px 23.7px -32px rgba(77, 77, 189, 0.6);
    background: linear-gradient(90deg, #4d4dbd 8.11%, #da5b3c 104.11%) padding-box,
        linear-gradient(to right, #4d4dbd, #da5b3c) border-box;
    border: 1px solid rgba(255, 255, 255, 0.4);
}
</style>
